import { Building2, TrendingUp, Award, DollarSign } from 'lucide-react';
import { egp } from '../../api';

type BranchStat = {
  id: string;
  name: string;
  city: string;
  isActive: boolean;
  completedWeeks: number;
  earnedEgp: number;
  activeWeeks: number;
  internWeeks: number;
};

type AnalyticsData = {
  earnedEgp: number;
  projectedEgp: number;
  completedWeeks: number;
  activeWeeks: number;
  internWeeks: number;
  totalRevenueEgp: number;
  certificatesIssued: number;
  earnedThisMonthEgp: number;
  studentsThisMonth: number;
  reservationsGrowthPct: number | null;
  branches?: BranchStat[];
};

export function ClinicAnalyticsPanel({ data }: { data: AnalyticsData }) {
  const branches = data.branches ?? [];
  const totalEarned = data.earnedEgp || 0;
  const internWeeks = data.internWeeks || 0;
  const fullCapacity = data.totalRevenueEgp || 0;
  const formatCount = (n: number) => n.toLocaleString('en-US');

  return (
    <div className="cd-analytics-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Overview Cards */}
      <div className="cd-stats cd-stats-3">
        <div className="cd-stat-card">
          <div className="cd-sc-top">
            <div className="cd-sc-label">Full capacity</div>
            <div className="cd-sc-icon cd-ic-orange">
              <DollarSign className="cd-ico" />
            </div>
          </div>
          <div className="cd-sc-val">{egp(totalEarned)}</div>
          {fullCapacity > 0 && (
            <div className="cd-sc-sub">{egp(fullCapacity)} agreed</div>
          )}
        </div>

        <div className="cd-stat-card">
          <div className="cd-sc-top">
            <div className="cd-sc-label">Intern weeks</div>
            <div className="cd-sc-icon cd-ic-navy">
              <TrendingUp className="cd-ico" />
            </div>
          </div>
          <div className="cd-sc-val">{formatCount(data.completedWeeks || 0)}</div>
          {internWeeks > 0 && (
            <div className="cd-sc-sub">of {formatCount(internWeeks)} agreed</div>
          )}
        </div>

        <div className="cd-stat-card">
          <div className="cd-sc-top">
            <div className="cd-sc-label">This Month</div>
            <div className="cd-sc-icon cd-ic-orange">
              <Award className="cd-ico" />
            </div>
          </div>
          <div className="cd-sc-val">{egp(data.earnedThisMonthEgp || 0)}</div>
        </div>
      </div>

      {/* Branch Breakdown Section */}
      <div className="cd-table-card" style={{ flex: 1 }}>
        <div className="cd-table-hd">
          <div>
            <span className="cd-table-title">Branches</span>
            <span className="cd-table-cnt">{branches.length}</span>
          </div>
        </div>

        <div className="cd-table-scroll">
          <table>
            <thead>
              <tr>
                <th>Branch Name</th>
                <th>City</th>
                <th>Active intern weeks</th>
                <th>Intern weeks</th>
                <th>Earned</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {branches.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: 'var(--cd-g400)' }}>
                    No branch data available.
                  </td>
                </tr>
              ) : (
                branches.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <div className="s-name" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Building2 size={14} style={{ color: 'var(--cd-navy)' }} />
                        {b.name}
                      </div>
                    </td>
                    <td>{b.city}</td>
                    <td>{formatCount(b.activeWeeks)}</td>
                    <td>{formatCount(b.completedWeeks)} / {formatCount(b.internWeeks || 0)}</td>
                    <td style={{ fontWeight: 700, color: 'var(--cd-g900)' }}>{egp(b.earnedEgp || 0)}</td>
                    <td>
                      <span className={`status-pill ${b.isActive ? 'sp-active' : 'sp-rated'}`}>
                        {b.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
